import { Edit, Upload } from "@carbon/icons-react";
import { Button, Stack } from "@carbon/react";
import { useModal } from "@flaminem/ui-components";
import { capitalize, toDate } from "@flaminem/utils";
import { FC } from "react";
import { generatePath, useNavigate, useParams } from "react-router-dom";

import { Scope, ScopeResource } from "src/authentication/PermissionsProvider.tsx";
import { usePermissions } from "src/authentication/usePermissions.ts";
import { ModalTransition } from "src/components/Modal";
import ScenarioRulesTable from "src/components/scenario/ScenarioRulesTable/ScenarioRulesTable.tsx";
import { useFormatDate, useT } from "src/internationalization";
import { ConfirmActivateVersionModal } from "src/pages/ScenarioDetails/ConfirmActivateVersionModal.tsx";
import { EditScenarioDraftVersionModal } from "src/pages/ScenarioDetails/EditScenarioDraftVersionModal/EditScenarioDraftVersionModal.tsx";
import { isDefinedScenarioRule, ScenarioVersionWithRules } from "src/types/scenario.ts";

type DraftScenarioVersionTabProps = {
  draftVersion: ScenarioVersionWithRules;
  scenarioId: string;
};

export const DraftScenarioVersionTab: FC<DraftScenarioVersionTabProps> = ({
  draftVersion,
  scenarioId,
}) => {
  const navigate = useNavigate();
  const { caseTypeCode } = useParams<{ caseTypeCode: string }>();

  const t = useT();
  const formatDate = useFormatDate();
  const { hasPermission } = usePermissions();

  const editVersionModal = useModal();
  const activateVersionModal = useModal();

  if (draftVersion)
    return (
      <>
        <div className="flex flex-col gap-5 pt-5 h-full">
          <div className="flex justify-between px-5">
            <div>
              <h4 className="cds--data-table-header__title">{t("scenario.tab.draft.title")}</h4>
              <p className="cds--data-table-header__description">
                {t("scenario.tab.draft.description")}
              </p>
            </div>
            {hasPermission(ScopeResource.SCENARIO, Scope.EDIT) && (
              <div>
                <Button
                  renderIcon={Edit}
                  onClick={editVersionModal.openModal}
                  kind="secondary"
                  className="mr-3"
                >
                  {t("general.actions.edit")}
                </Button>
                <Button
                  id="activateVersionButton"
                  renderIcon={Upload}
                  onClick={activateVersionModal.openModal}
                >
                  {t("scenario.tab.draft.button.activate")}
                </Button>
              </div>
            )}
          </div>
          <Stack gap={3} className="px-5">
            <div>
              <p>
                <span className="text-label02 text-textSecondary leading-label02 tracking-label02 font-label02">
                  {t("general.form.name.label")}:{" "}
                </span>
                {draftVersion.name}
              </p>
              <p>
                <span className="text-label02 text-textSecondary leading-label02 tracking-label02 font-label02">
                  {t("scenario.tab.label.threshold")}:{" "}
                </span>
                {draftVersion.threshold}
              </p>
              <p>
                <span className="text-label02 text-textSecondary leading-label02 tracking-label02 font-label02">
                  {t("scenario.tab.label.date")}:{" "}
                </span>
                {formatDate(toDate(draftVersion.adminStatus.creation), {
                  dateStyle: "medium",
                })}
              </p>
              {draftVersion.description && (
                <div className="line-clamp-3 text-body02 leading-body02 tracking-body02 font-body02">
                  <span className="text-label02 text-textSecondary leading-label02 tracking-label02 font-label02">
                    {t("general.table.description.column.title")}:{" "}
                  </span>
                  {capitalize(draftVersion.description)}
                </div>
              )}
            </div>
          </Stack>
          <ScenarioRulesTable
            rules={draftVersion.rules.filter(isDefinedScenarioRule).map((details) => ({
              ...details.rule,
              score: details.score,
            }))}
          />
        </div>
        <ModalTransition>
          {editVersionModal.isModalOpen && (
            <EditScenarioDraftVersionModal
              scenarioId={scenarioId}
              draftVersion={draftVersion}
              onClose={editVersionModal.closeModal}
            />
          )}
        </ModalTransition>
        <ModalTransition>
          {activateVersionModal.isModalOpen && (
            <ConfirmActivateVersionModal
              scenarioId={scenarioId}
              draftVersion={draftVersion}
              onClose={activateVersionModal.closeModal}
              onSubmit={() => {
                activateVersionModal.closeModal();
                navigate(
                  generatePath("/scenario/:caseTypeCode/:id/active", {
                    caseTypeCode: caseTypeCode!,
                    id: scenarioId,
                  })
                );
              }}
            />
          )}
        </ModalTransition>
      </>
    );
};
